/**
 * Translates Branch offer values into the wording EZLynx uses, for the EasyKey clipboard
 * (build-easykey-clipboard.js), which the EasyKey Chrome extension pastes into EZLynx's quoting screens.
 *
 * Most of these were first worked out for the EasyKey XML export (#31015, #31203), which EZLynx's
 * V200 import validated value by value; that export has since been replaced by the extension
 * (#29109). EZLynx's screens use the same vocabulary, and the extension matches it ignoring case and
 * punctuation ("WOOD SHINGLES" picks "Wood Shingles").
 *
 * GitHub issue: #29109
 */

import { policyTypes } from '@ourbranch/policy-types';

import { EZLYNX_PRIOR_CARRIERS } from './ezlynx-prior-carriers';

export function formatDate(dateStr) {
  if (!dateStr) {
    return '';
  }
  return String(dateStr).slice(0, 10);
}

export function splitAddress(address, address2) {
  if (!address) {
    return { streetNumber: '', streetName: '', unitNumber: address2 || '' };
  }
  const trimmed = String(address).trim();
  const match = trimmed.match(/^(\d+\S*)\s+(.+)$/);
  if (match) {
    return { streetNumber: match[1], streetName: match[2], unitNumber: address2 || '' };
  }
  return { streetNumber: '', streetName: trimmed, unitNumber: address2 || '' };
}

export function splitZip(zip) {
  if (!zip) {
    return { zip5: '', zip4: '' };
  }
  const digitsOnly = String(zip).replace(/\D/g, '');
  if (digitsOnly.length >= 9) {
    return { zip5: digitsOnly.slice(0, 5), zip4: digitsOnly.slice(5, 9) };
  }
  return { zip5: digitsOnly.slice(0, 5), zip4: '' };
}

export function deriveHomePolicyType(selectedOption) {
  if (!selectedOption) {
    return 'HO3';
  }
  if (selectedOption === policyTypes.Renters || selectedOption === policyTypes.ARBundle) {
    return 'HO4';
  }
  if (selectedOption === policyTypes.Condo || selectedOption === policyTypes.CABundle) {
    return 'HO6';
  }
  return 'HO3';
}

export function isBundle(selectedOption) {
  if (!selectedOption) {
    return false;
  }
  return [policyTypes.HABundle, policyTypes.ARBundle, policyTypes.CABundle, policyTypes.FABundle].includes(
    selectedOption
  );
}

// Branch stores gender / maritalStatus as single-letter codes (M, F, S, D, W); EZLynx lists the full
// word ("Male", "Married"). Either form is accepted.
export function normalizeGender(value) {
  if (!value) {
    return '';
  }
  const v = String(value).trim().toUpperCase();
  if (v === 'M' || v === 'MALE') {
    return 'Male';
  }
  if (v === 'F' || v === 'FEMALE') {
    return 'Female';
  }
  return value;
}

export function normalizeMaritalStatus(value) {
  if (!value) {
    return '';
  }
  const v = String(value).trim().toUpperCase();
  if (v === 'M' || v === 'MARRIED') {
    return 'Married';
  }
  if (v === 'S' || v === 'SINGLE') {
    return 'Single';
  }
  if (v === 'D' || v === 'DIVORCED') {
    return 'Divorced';
  }
  if (v === 'W' || v === 'WIDOWED') {
    return 'Widowed';
  }
  if (v === 'P' || v === 'SEPARATED') {
    return 'Separated';
  }
  return value;
}

// 'SP' (Spouse) in either of the relationship codes a driver can carry: `relationshipCode` is the
// rater's TransUnion relationship and `relationshipToInsured` the one staff enter for vehicle
// ownership. Staff's offer query doesn't fetch `relationshipCode`, so in Staff only the second
// can be set.
function isSpouseDriver(driver) {
  return driver.relationshipCode === 'SP' || driver.relationshipToInsured === 'SP';
}

// Branch's co-applicant is the driver flagged `isCoApplicant`: the rater flags the spouse (by
// relationship code, else the driver closest in age to the primary, see
// packages/rater/src/retrieve-data-auto.js) and staff can change it on the offer. A driver coded as
// spouse is the fallback for offers without the flag. Using `maritalStatus === 'Married'` would
// false-positive on adult children / parents who are also married but not to the insured.
export function findCoApplicantDriver(drivers) {
  if (!Array.isArray(drivers)) {
    return null;
  }
  const otherDrivers = drivers.filter((d) => !d.isPrimary);
  return otherDrivers.find((d) => d.isCoApplicant) || otherDrivers.find(isSpouseDriver) || null;
}

export function deriveDriverRelation(driver, isPrimary) {
  if (isPrimary) {
    return 'Insured';
  }
  // The co-applicant is the insured's spouse in Branch's model (see findCoApplicantDriver).
  if (driver.isCoApplicant || isSpouseDriver(driver)) {
    return 'Spouse';
  }
  // Branch doesn't store granular non-spouse relationships consistently, so
  // default to 'Child' for all other dependents.
  return 'Child';
}

// Branch stores most home fields as lookup codes. These maps translate either the Branch code or the
// Branch display value into EZLynx's wording. A Branch value with no EZLynx equivalent is left out of
// its map, so nothing is pasted for it rather than a guess.
export const BRANCH_TO_EZLYNX_DWELLING = {
  1: 'One Family',
  3: 'Town Home',
  4: 'Other',
  'Single Home': 'One Family',
  'Town Home': 'Town Home',
  Other: 'Other'
};
export const BRANCH_TO_EZLYNX_CONSTRUCTION = {
  B: 'Brick',
  F: 'Frame',
  Brick: 'Brick',
  Frame: 'Frame'
};
// Branch's roofType doesn't record tile sub-types (clay / concrete / spanish), so "T" is generic
// "TILE"; Arizona needs a sub-type (see translateRoofToEzlynx).
const BRANCH_TO_EZLYNX_ROOF = {
  W: 'WOOD SHINGLES',
  S: 'SLATE',
  T: 'TILE',
  M: 'METAL',
  C: 'ASPHALT SHINGLES',
  O: 'OTHER',
  Wood: 'WOOD SHINGLES',
  Slate: 'SLATE',
  Tile: 'TILE',
  Metal: 'METAL',
  Composition: 'ASPHALT SHINGLES',
  Other: 'OTHER'
};
export const BRANCH_TO_EZLYNX_ROOF_DESIGN = {
  G: 'Gable',
  H: 'Hip',
  F: 'Flat',
  O: 'Other'
};
export const BRANCH_TO_EZLYNX_DWELLING_USE = {
  P: 'Primary',
  PS: 'Primary',
  S: 'Secondary'
};
// Branch's exteriorWallType (publicLookups.json). Branch's "S" ("Siding - Other (non-wood)") has no
// EZLynx equivalent, so it's left out.
export const BRANCH_TO_EZLYNX_EXTERIOR_WALLS = {
  B: 'Brick Veneer',
  AL: 'Siding, Aluminum',
  ASB: 'Siding, Hardboard',
  T111: 'Siding, T-111',
  VL: 'Siding, Vinyl',
  T: 'Stucco',
  W: 'Siding, Wood'
};

export function translateToEzlynx(map, value) {
  if (value == null || value === '') {
    return '';
  }
  return map[value] || '';
}

// EZLynx rejects a generic tile roof for Arizona and requires a sub-type; concrete tile is the one
// verified for AZ homes.
export function translateRoofToEzlynx(roofType, state) {
  const base = translateToEzlynx(BRANCH_TO_EZLYNX_ROOF, roofType);
  if (base === 'TILE' && state === 'AZ') {
    return 'TILE(CONCRETE)';
  }
  return base;
}

// Splits Branch's three-part BIPD limit ("100/300/100") the way EZLynx asks for it: bodily injury in
// thousands ("100/300") and property damage in dollars ("100000").
export function splitBIPD(bipd) {
  if (!bipd) {
    return { bi: '', pd: '' };
  }
  const parts = String(bipd).split('/');
  const toFullDollars = (thousands) => {
    const n = parseInt(thousands, 10);
    return Number.isFinite(n) ? String(n * 1000) : '';
  };
  if (parts.length >= 3) {
    return { bi: `${parts[0]}/${parts[1]}`, pd: toFullDollars(parts[2]) };
  }
  if (parts.length === 2) {
    return { bi: `${parts[0]}/${parts[1]}`, pd: '' };
  }
  return { bi: parts[0] || '', pd: '' };
}

// Branch stores "NONE" / "0" for no medical payments coverage; EZLynx calls it "None".
export function normalizeMpValue(value) {
  if (!value) {
    return 'None';
  }
  const s = String(value).trim();
  if (s === '' || s === '0' || s.toUpperCase() === 'NONE') {
    return 'None';
  }
  return s;
}

// Branch stores "0/0" or "NONE" when the insured carries only the state-mandated minimum UM/UIM;
// EZLynx's answer for that is "State Minimum".
export function normalizeUMUIMValue(value) {
  if (!value) {
    return '';
  }
  const s = String(value).trim();
  if (s === '' || s === '0/0' || s.toUpperCase() === 'NONE') {
    return 'State Minimum';
  }
  return s;
}

// EZLynx lists rental coverage as per-day / 30-day maximum ("30/900", "40/1200"); Branch stores the
// per-day rate ("40").
export function normalizeRentalDeductible(limitRental) {
  if (!limitRental) {
    return 'No Coverage';
  }
  const s = String(limitRental).trim();
  if (s === '' || s === '0' || s.toUpperCase() === 'NONE') {
    return 'No Coverage';
  }
  const perDay = parseInt(s, 10);
  if (!Number.isFinite(perDay)) {
    return 'No Coverage';
  }
  return `${perDay}/${perDay * 30}`;
}

// Branch stores "NONE" / "0" / empty when comp or collision isn't carried; EZLynx calls it
// "No Coverage". Branch's deductibleComprehensive also has composites like "1000/0GL" ($1,000 comp
// with a $0 glass deductible) and "1000/50GL"; EZLynx's deductible is the leading amount, and full
// glass is a separate checkbox (see build-easykey-clipboard.js). deductibleCollision never has a slash.
export function normalizeDeductible(value) {
  if (value == null || value === '') {
    return 'No Coverage';
  }
  const s = String(value).trim();
  if (s === '' || s === '0' || s.toUpperCase() === 'NONE') {
    return 'No Coverage';
  }
  const slashIdx = s.indexOf('/');
  if (slashIdx > 0) {
    return s.slice(0, slashIdx);
  }
  return s;
}

// Elapsed { years, months } from `startDate` to today; zeros when startDate is missing or invalid.
export function elapsedYearsMonths(startDate) {
  if (!startDate) {
    return { years: 0, months: 0 };
  }
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) {
    return { years: 0, months: 0 };
  }
  const now = new Date();
  let totalMonths = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) {
    totalMonths -= 1;
  }
  if (totalMonths < 0) {
    totalMonths = 0;
  }
  return { years: Math.floor(totalMonths / 12), months: totalMonths % 12 };
}

// Case-insensitive lookup over EZLynx's prior carrier list, built once at module load.
const PRIOR_CARRIER_LOOKUP = new Map(EZLYNX_PRIOR_CARRIERS.map((c) => [c.toUpperCase(), c]));

// Carriers sorted by length descending for the substring scan. Longest matches win, so a Branch
// string like "DIRECT GENERAL AUTO" prefers "Direct General" over the shorter "Direct".
const PRIOR_CARRIER_BY_LENGTH_DESC = [...EZLYNX_PRIOR_CARRIERS].sort((a, b) => b.length - a.length);

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Maps Branch's free-text carrier strings ("PROGRESSIVE GROUPS", "STATE FARM MUTUAL", "GEICO
// INDEMNITY CO"), whatever the data pull returned, onto EZLynx's prior carrier list.
//
// Resolution order:
//   1. Exact case-insensitive match.
//   2. Strip noise suffixes (GROUP[S], INSURANCE/INS, INC, CO, COMPANY, MUTUAL, CORP) and retry, so
//      "PROGRESSIVE GROUPS" → "Progressive" and "STATE FARM MUTUAL" → "State Farm".
//   3. Longest whole-word substring scan against the original input. A prefix match mis-matched
//      "THE HARTFORD" → "The General" and "DIRECT AUTO" → "Direct General"; word-bounded substring
//      matching is order-independent and prefers specific matches.
//   4. "Other Standard", EZLynx's catch-all, so the prior insurance is still recorded.
export function normalizePriorCarrier(branchCarrier) {
  if (!branchCarrier) {
    return '';
  }
  const upper = String(branchCarrier).trim().toUpperCase();
  if (!upper) {
    return '';
  }

  const direct = PRIOR_CARRIER_LOOKUP.get(upper);
  if (direct) {
    return direct;
  }

  const stripped = upper
    .replace(/\s+(GROUPS?|INSURANCE|INS|INC|CO|COMPANY|MUTUAL|CORP)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const stripMatch = PRIOR_CARRIER_LOOKUP.get(stripped);
  if (stripMatch) {
    return stripMatch;
  }

  // Against the ORIGINAL input, not the stripped one, so "STANDARD MUTUAL" can still match
  // "Standard Mutual" instead of being stripped to "STANDARD" and falling to "Other Standard".
  for (const candidate of PRIOR_CARRIER_BY_LENGTH_DESC) {
    const pattern = new RegExp(`(^|\\s)${escapeRegex(candidate.toUpperCase())}(\\s|$)`);
    if (pattern.test(upper)) {
      return candidate;
    }
  }

  return 'Other Standard';
}

// EZLynx's "Years with Prior Carrier" dropdown runs "0" to "15", then "More than 15". Past the cap
// the months mean nothing, so they're "0".
export function formatPriorCarrierDuration({ years, months } = {}) {
  const y = Math.floor(Number(years) || 0);
  const m = Math.min(11, Math.max(0, Math.floor(Number(months) || 0)));
  if (y > 15) {
    return { years: 'More than 15', months: '0' };
  }
  return { years: String(y), months: String(m) };
}

// Durations come back as zeros when the start dates are missing; the clipboard only pastes them when
// Branch has a start date.
export function derivePriorAutoPolicy(global) {
  const g = global || {};
  return {
    priorCarrier: g.currentlyAutoInsured ? normalizePriorCarrier(g.currentAutoCarrier) : '',
    expiration: formatDate(g.mostRecentAutoInsuranceCoverageEndDate || g.currentAutoCarrierEndDate),
    withCarrier: formatPriorCarrierDuration(elapsedYearsMonths(g.currentAutoCarrierStartDate)),
    continuous: formatPriorCarrierDuration(
      elapsedYearsMonths(g.continuousAutoCoverageStartDate || g.currentAutoCarrierStartDate)
    )
  };
}

// Branch overloads `homeCoverage.deductibleAllOther`: values < 1 are fractions (0.01 = 1% of
// Coverage A), values >= 1 are dollar amounts (the same `< 1` threshold the offer validators use).
// EZLynx lists percentage deductibles as "1%", so a fraction becomes a percentage.
export function formatHomeDeductible(value) {
  if (value == null || value <= 0) {
    return '';
  }
  if (value < 1) {
    const pct = Math.round(value * 100);
    return pct > 0 ? `${pct}%` : '';
  }
  return String(Math.round(value));
}
